import { ValidationRule } from '@vuelidate/core';

import { TDatePickerMode } from '@/common/components/DateComponent/dateComponentMode';
import { MenuModes, TTreeOption } from '@/common/components/redesigned/TreeMultiselect/types';
import { TProductItem, TProductsTreeFetcher } from '@/common/components/redesigned/TreeSelectionProducts/types';

export type TFilterConfig<T extends object = Record<string, unknown>> =
  | TFilterText
  | TFilterSelect<T>
  | TFilterDate
  | TFilterTime
  | TFilterRangeNumber
  | TFilterTreeSelect
  | TFilterMultiSelect<T>
  | TFilterProduct
  | TFilterCheckbox;

export type TFilterType =
  | 'text'
  | 'select'
  | 'multi-select'
  | 'tree-select'
  | 'date'
  | 'time'
  | 'rangeNumber'
  | 'product'
  | 'checkbox';

export type TBaseFilter = {
  type: TFilterType;
  vModel: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  disabledWhen?: (filter: TFilter) => boolean;
  isKeepValueOnDisable?: boolean;
  multiple?: boolean;
};

type TFilterWithValidations = TBaseFilter & {
  textError?: string;
  customValidations?: Record<string, ValidationRule>;
};

export type TFilterText = TBaseFilter & {
  type: 'text';
  placeholder?: string;
  maxLength?: number;
  clearable?: boolean;
};

export type TFilterCheckbox = TBaseFilter & {
  type: 'checkbox';
};

export type TBaseFilterSelect<T extends object = Record<string, unknown>> = {
  placeholder?: string;
  options: T[];
  valueProp: keyof T;
  labelProp: keyof T;
  clearable?: boolean;
  maxLength?: number;
  filters?: {
    source: string;
    key: string;
  }[];
};

export type TFilterSelect<T extends object = Record<string, unknown>> = TBaseFilter &
  TBaseFilterSelect<T> & {
    type: 'select';
    loadingList?: boolean;
  };

export type TFilterMultiSelect<T extends object = Record<string, unknown>> = TFilterWithValidations &
  TBaseFilterSelect<T> & {
    type: 'multi-select';
  };

export type TFilterTreeSelect<T extends object = TTreeOption> = TFilterWithValidations &
  Partial<Pick<TBaseFilterSelect<T>, 'valueProp' | 'labelProp'>> & {
    type: 'tree-select';
    options: T[];
    isSingleMode?: boolean;
    levelEventsLabels?: readonly string[];
    placeholder?: string;
    childrenProp?: string;
    noResultsText?: string;
    noOptionsText?: string;
    menuMode?: MenuModes;
  };

export type TBaseFilterRange = TBaseFilter & {
  range?: {
    prefix?: {
      from: string;
      to: string;
    };
    postfix?: {
      from: string;
      to: string;
    };
  };
};

export type TBaseFilterDates = TBaseFilterRange & {
  placeholders?: [string, string];
  typeable?: boolean;
  mode?: TDatePickerMode;
  limits?: {
    from?: string;
    to?: string;
  };
  hasInputMask?: boolean;
};

export type TFilterDate = TBaseFilterDates & {
  type: 'date';
};

export type TFilterTime = TBaseFilter & {
  type: 'time';
};

export type TFilterRangeNumber = TBaseFilter &
  TBaseFilterRange & {
    type: 'rangeNumber';
    placeholderFrom?: string;
    placeholderTo?: string;
  };

export type TFilterProduct = TBaseFilter & {
  type: 'product';
  fetcher?: TProductsTreeFetcher;
  valueUpdated?: (value: TProductItem[]) => void;
};

// Конфигурация одной кнопки действий фильтра (для переиспользуемого FilterActions).
export type TFilterActionButton = {
  text: string;
  mod?: string;
  class?: string;
  disabled?: boolean;
  isVisible?: boolean;
  onClick: () => void;
};

export type TFilterValue = string | number | boolean | (string | number)[] | undefined;

export type TFilter = Record<string, TFilterValue>;

export enum DatesLimits {
  UPPER = 1,
  LOWER = 2,
}

export enum RangeTypes {
  FROM = 'From',
  TO = 'To',
}

/**
 * @description Постфиксы для ключей фильтров (для постановки в vModel)
 * @example product => productCodes
 *          product => productIds
 */
export enum FilterPostfixKey {
  CODES = 'Codes',
  IDS = 'Ids',
}
